/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';

import { env } from '@amfam/environment';
import type { DigitalAccount } from '@amfam/shared/digital-account/data-access/lib/+state/digital-account/digital-account.model';
import { selectIsShellAccount } from '@amfam/shared/digital-account/data-access/lib/+state/digital-account/digital-account.selectors';
import { Party } from '@amfam/shared/models/lib/party/party';
import { PartyEmail } from '@amfam/shared/models/lib/party/party-email';
import { UpdateParty } from '@amfam/shared/user/lib/+state/user.actions';
import { selectCustomerId, selectPartyVersion } from '@amfam/shared/user/lib/+state/user.selectors';
import { Applications } from '@amfam/shared/utility/shared-services/lib/application/application.enum';
import { ApplicationService } from '@amfam/shared/utility/shared-services/lib/application/application.service';

import type {
  ContactEntryActionPayload,
  DeleteContactEntryActionPayload,
  Entry,
  MethodType
} from '../+state/profile.actions';

const RETRY_DELAY_MS = 10_000;
const GATEWAY_ERROR_STATUSES: readonly number[] = [502, 503, 504];

interface PartyVersionResponse {
  party?: { partyVersion?: string };
}

@Injectable()
export class PartyService {
  private http = inject(HttpClient);
  private store = inject(Store);
  private applicationService = inject(ApplicationService);
  private destroyRef = inject(DestroyRef);
  private customerId = '';
  private endpoint = '';
  private otherEntry: any;
  private partyVersion = '';

  constructor() {
    this.store
      .select(selectCustomerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(customerId => {
        if (customerId) {
          this.customerId = customerId;
          this.endpoint = env.userAccountApi + '/parties/' + customerId;

          this.getPartyDetails().subscribe({ next: () => {}, error: () => {} });
        }
      });

    // Single source of truth to get the party version
    this.store
      .select(selectPartyVersion)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(partyVersion => {
        this.partyVersion = partyVersion;
      });
  }

  addContactEntry(payload: ContactEntryActionPayload): Observable<any> {
    const { data: dataProp, methodType } = payload;
    let data = dataProp;
    if (methodType === 'emails') {
      data = {
        emailAddress: dataProp.emailAddress,
        contactMethodUsages: dataProp.contactMethodUsages
      } as PartyEmail;
    }
    const options = this.getOptions();

    const url = this.endpoint + '/' + methodType;
    const body = {
      authId: env.processId,
      party: {
        partyVersion: this.partyVersion,
        sourceSystemInformation: {
          sourceSystemName: 'MYACCOUNT',
          sourceSystemIdentifier: this.customerId
        },
        contactMethodDetail: {
          [methodType]: [data]
        }
      }
    };
    return this.http.post(url, body, options);
  }

  editContactEntry(
    entry: Entry,
    methodType: MethodType,
    entry2: Entry | undefined
  ): Observable<any> {
    // if we need to change a second entry as well, we store the first one so we can pass it to the store later
    this.otherEntry = entry2 ? entry : this.otherEntry;
    // extract id via this check because their id property names are different
    const id = methodType === 'phones' ? 'phoneId' : 'emailId';
    const url = `${this.endpoint}/${methodType}/${(entry as any)?.[id]}`;
    const body = {
      authId: env.processId,
      party: {
        partyVersion: this.partyVersion,
        sourceSystemInformation: {
          sourceSystemName: 'MYACCOUNT',
          sourceSystemIdentifier: this.customerId
        },
        contactMethodDetail: {
          [methodType]: [entry]
        }
      }
    };
    const options = this.getOptions();
    return this.http.put(url, body, options).pipe(
      map((data: any) => {
        // update the local copy of the partyVersion
        if (data?.partyResourceIdentifier?.partyVersion != null) {
          this.partyVersion = data.partyResourceIdentifier.partyVersion;
        }
        // add our first entry to the response so the store can update it
        data.otherEntry = this.otherEntry ? this.otherEntry : undefined;
        // reset value
        this.otherEntry = entry2 ? this.otherEntry : false;
        return data;
      })
    );
  }

  deleteContactEntry({ data, methodType }: DeleteContactEntryActionPayload): Observable<any> {
    const url = `${this.endpoint}/${methodType}/${data}`;
    const options = this.getOptions({
      'AFI-PartyVersion': this.partyVersion,
      'AFI-AuthId': env.processId,
      'AFI-SourceSystemName': 'MYACCOUNT',
      'AFI-SourceSystemIdentifier': this.customerId
    });
    return this.http.delete(url, options);
  }

  refreshPartyVersion(): Observable<void> {
    if (!this.endpoint) {
      return of(undefined);
    }
    const url = this.endpoint;
    const options = this.getOptions({
      authId: env.processId,
      userId: env.processId
    });
    return this.http.get<PartyVersionResponse>(url, options).pipe(
      retry({
        count: 1,
        delay: (error: HttpErrorResponse): Observable<number> => {
          if (GATEWAY_ERROR_STATUSES.includes(error.status)) {
            return timer(RETRY_DELAY_MS);
          }
          throw error;
        }
      }),
      map(response => {
        if (response?.party?.partyVersion != null) {
          this.partyVersion = response.party.partyVersion;
        }
      }),
      catchError((err: HttpErrorResponse) => {
        console.warn(
          '[PartyService] refreshPartyVersion failed, proceeding with cached version',
          err.status
        );
        return of(undefined);
      })
    );
  }

  getPartyDetails(): Observable<Party> {
    const url = this.endpoint;
    const options = this.getOptions({
      authId: env.processId,
      userId: env.processId
    });
    return this.http.get(url, options).pipe(
      retry({
        count: 1,
        delay: (error: HttpErrorResponse): Observable<number> => {
          if (GATEWAY_ERROR_STATUSES.includes(error.status)) {
            return timer(RETRY_DELAY_MS);
          }
          throw error;
        }
      }),
      map((data: any) => {
        let updatedPartyStoreObj: Party;
        if (data.party) {
          updatedPartyStoreObj = new Party(data.party);
          if (this.applicationService.isApp(Applications.MYACCOUNT_ADMIN)) {
            const sa = this.store.selectSignal(selectIsShellAccount)();
            // updating primary indicator
            if (sa) {
              updatedPartyStoreObj.emails.forEach(email => {
                // likely is not working - type is boolean yet trying to get `emailAddress` from it
                email.primaryIndicator =
                  email.emailAddress === (sa as unknown as DigitalAccount).emailAddress;
              });
            }
            // dispatch action to update party bucket in the user object
            this.store.dispatch(UpdateParty(updatedPartyStoreObj));
          } else {
            // dispatch action to update party bucket in the user object
            this.store.dispatch(UpdateParty(updatedPartyStoreObj));
          }
          return updatedPartyStoreObj;
        }
        // if party isn't in the response something went wrong
        return new Party();
      })
    );
  }

  // AFI-UserId is required on every party request, so it lives here rather than at each call site
  private getOptions(headerObj: Record<string, string> = {}) {
    const headers = new HttpHeaders({ ...headerObj, 'AFI-UserId': env.processId });
    return { headers: headers };
  }
}
